import { Skeleton, type SxProps, type Theme } from '@mui/material';
import type { PropsWithChildren } from 'react';
import React, { Suspense, useMemo } from 'react';
import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useCustomer } from '../../../hooks/requests/use-customer';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { CustomersPageNavigationLayout } from '../../customers/CustomersPage/customers-page-components/CustomerLayout/customer-layout-components/CustomersPageLayout/CustomersPageNavigationLayout';
import { CustomerInvoicePageTabsAction } from './customer-invoice-page-components/CustomerInvoicePageTabsAction';
import {
  CUSTOMER_INVOICE_PAGE_TAB_OPTIONS,
  useCustomerInvoicePageTabs,
} from './customer-invoice-page-hooks/use-customer-invoice-page-tabs';

const CustomerInvoicePage: React.FC<PropsWithChildren> = ({ children }) => {
  const { customerId } = useValidatedParams<['customerId']>('customerId');
  const { data } = useCustomer(customerId, { suspense: false });
  const [tabs, currentTab] = useCustomerInvoicePageTabs();
  const navigate = useVyNavigate();

  const layoutStyle: SxProps<Theme> = useMemo(() => {
    if (currentTab === CUSTOMER_INVOICE_PAGE_TAB_OPTIONS.PreviewInvoice) {
      return {
        padding: 0,
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
      };
    }

    return {
      padding: 0,
      paddingBottom: '48px',
    };
  }, [currentTab]);

  return (
    <VyLayout
      navigation={
        <CustomersPageNavigationLayout
          customer={data?.customer}
          relatedCustomers={data?.children}
          onBackClick={() => navigate(-1)}
        />
      }
      tabs={tabs}
      currentTab={currentTab}
      sx={layoutStyle}
      tabsAction={
        <div className="flex justify-center items-end pb-1.5 ml-auto gap-x-2">
          <Suspense fallback={<Skeleton variant="rectangular" width={160} height={30} />}>
            <CustomerInvoicePageTabsAction />
          </Suspense>
        </div>
      }
    >
      {children}
    </VyLayout>
  );
};

const CustomerInvoicePageWithErrorBoundary = withSuspenseBoundary(CustomerInvoicePage);
export { CustomerInvoicePageWithErrorBoundary as CustomerInvoicePage };
